import { useCallback, useMemo } from 'react';
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  AiComponentListGroupByField,
  AiComponentListGroupDto,
  AiComponentsListQueryInput,
} from '@terra/shared-contracts-ai-applications';
import { terraProxyApiClient } from '../../../clients/testerClient';
import { AI_APPLICATION_COMPONENTS_KEY } from '../../../hooks/api/useAiApplicationComponents';
import {
  AI_COMPONENTS_PAGE_SIZE,
  mergeUniqueById,
} from './ai-components-table.model';

const STALE_TIME_MS = 30_000;
const NO_GROUPS: readonly AiComponentListGroupDto[] = [];

export function useAiComponentsListData({
  applicationId,
  query,
}: {
  applicationId: string;
  query: AiComponentsListQueryInput;
}) {
  const queryClient = useQueryClient();
  const pagesQuery = useInfiniteQuery({
    queryKey: [AI_APPLICATION_COMPONENTS_KEY, applicationId, 'pages', query],
    queryFn: ({ pageParam }) =>
      terraProxyApiClient.getAiApplicationComponents(applicationId, {
        ...query,
        skip: pageParam,
        limit: AI_COMPONENTS_PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce(
        (sum, page) => sum + page.components.length,
        0,
      );

      return lastPage.components.length === AI_COMPONENTS_PAGE_SIZE &&
        loaded < (lastPage.totalCount ?? 0)
        ? loaded
        : undefined;
    },
    enabled: Boolean(applicationId),
    staleTime: STALE_TIME_MS,
  });
  const pages = pagesQuery.data?.pages;
  const components = useMemo(
    () => mergeUniqueById((pages ?? []).flatMap((page) => page.components)),
    [pages],
  );
  const firstPage = pages?.[0];
  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: [AI_APPLICATION_COMPONENTS_KEY, applicationId],
    });
  }, [applicationId, queryClient]);
  const fetchNextPage = pagesQuery.fetchNextPage;
  const loadMore = useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);

  return {
    components,
    groups: firstPage?.groups ?? NO_GROUPS,
    totalCount: firstPage?.totalCount ?? 0,
    isLoading: pagesQuery.isLoading,
    isRefetching: pagesQuery.isRefetching,
    isError: pagesQuery.isError,
    isFetchingMore: pagesQuery.isFetchingNextPage,
    hasMore: Boolean(pagesQuery.hasNextPage),
    loadMore,
    refresh,
  };
}

export function useAiComponentsFacet({
  applicationId,
  query,
  groupBy,
  enabled = true,
}: {
  applicationId: string;
  query: AiComponentsListQueryInput;
  groupBy: AiComponentListGroupByField;
  enabled?: boolean;
}) {
  const facetQuery = useQuery({
    queryKey: [
      AI_APPLICATION_COMPONENTS_KEY,
      applicationId,
      'facet',
      groupBy,
      query,
    ],
    queryFn: () =>
      terraProxyApiClient.getAiApplicationComponents(applicationId, {
        ...query,
        groupBy,
        skip: 0,
        limit: 1,
      }),
    enabled: Boolean(applicationId) && enabled,
    staleTime: STALE_TIME_MS,
  });

  return {
    groups: facetQuery.data?.groups ?? NO_GROUPS,
    segmentCounts: facetQuery.data?.segmentCounts,
  };
}
